import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useRef, useState, useEffect } from 'react';

import { useTranslate } from 'src/locales';
import { useFilterChannelMutation } from 'src/pages/channel/slice/channelApi';
// import { createChannelTransform } from 'src/pages/channel/utils/transform-channel-data';
// import { useFilterTeamMutation } from 'src/pages/setting/components/teams/slice/teamApi';
// import { useFilterUserDetailMutation } from 'src/pages/setting/components/users/slice/userApi';
import {
  useFilterContactMutation,
  useGetContactDtlMutation,
} from 'src/pages/contacts/slice/contactsApi';
import {
  useFilterAccountMutation,
  useGetAccountDtlMutation,
} from 'src/pages/accounts/slice/accountsApi';
import {
  useGetMediaUrlMutation,
  useFilterConversationMutation,
  useConversationDetailsMutation,
} from 'src/pages/chat/slice/chatApi';

import { useTableConfig } from 'src/components/table';

type RecordType = '' | 'Contact' | 'Channel' | 'Account' | 'User' | 'Team' | 'Conversation';

type UseSelectRecordProps = {
  type: RecordType;
  isAccountView?: boolean;
  currentContactId?: string | null;
  isOnlyWaChannel?: boolean;
  defaultValue?: string;
  name: string;
  setValue: (name: string, value: string, options?: { shouldValidate?: boolean }) => void;
  setSelectedUserAccDtl: (data: any) => void;
  onItemSelect?: (item: any) => void;
};

export function useSelectRecord({
  type,
  isAccountView = false,
  currentContactId = null,
  isOnlyWaChannel = false,
  defaultValue = '',
  name,
  setValue,
  setSelectedUserAccDtl,
  onItemSelect,
}: UseSelectRecordProps) {
  const { t } = useTranslate('translation');

  const [openDialog, setOpenDialog] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [dropdownData, setDropdownData] = useState<any[]>([]);
  const [selectedLabel, setSelectedLabel] = useState(defaultValue || '');
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  
  const lastFetchedIdRef = useRef<string | null>(null);

  // API hooks
  const [filterContact, { isLoading: isContactLoading }] = useFilterContactMutation();
  const [filterChannel, { isLoading: isChannelLoading }] = useFilterChannelMutation();
  const [filterAccount, { isLoading: isAccountLoading }] = useFilterAccountMutation();
  // const [filterTeam, { isLoading: isTeamLoading }] = useFilterTeamMutation();
  // const [filterUserList, { isLoading: isUserLoading }] = useFilterUserDetailMutation();
  const [getMediaUrl, { isLoading: isgetMediaLoading }] = useGetMediaUrlMutation();
  const [getAccountDtl] = useGetAccountDtlMutation();
  const [getContactDtl] = useGetContactDtlMutation();
  const [filterConversation, { isLoading: isConversationLoading }] =
    useFilterConversationMutation();
  const [getConversationDtl] = useConversationDetailsMutation();

  const isLoader =
    isContactLoading ||
    isChannelLoading ||
    isAccountLoading ||
    // isTeamLoading ||
    // isUserLoading ||
    isgetMediaLoading ||
    isConversationLoading 

  // Field configuration
  const fldListObj = {
    Contact: ['firstName', 'lastName', 'phone', 'email', 'country'],
    Channel: ['channelName'],
    Account: ['name', 'email', 'phone', 'crNumber', 'city', 'country'],
    User: ['firstName', 'lastName', 'email'],
    Team: ['teamName', 'createdDate', 'status'],
    Conversation: ['displayName', 'lastMessage', 'status', 'createdAt'],
  };

  const orgFldNames = {
    Team: { teamName: 'name', createdDate: 'createdAt', status: 'status' },
  };

  const fields = fldListObj[type as keyof typeof fldListObj];
  if (!fields) throw new Error(`Filter Fields Not found => ${type}`);

  const tableConfig = useTableConfig({
    moduleName: type?.toUpperCase() || 'CONTACT',
    fields,
    fieldMap: type === 'Team' ? orgFldNames.Team : undefined,
    defaultSort: 'createdAt',
  });

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const getApiFunction = () => {
    const apiFuncObj = {
      Account: filterAccount,
      Channel: filterChannel,
      Contact: filterContact,
      Conversation: filterConversation,
    };
    return apiFuncObj[type as keyof typeof apiFuncObj];
  };

  const processApiResponse = (res: any) =>
    res?.data && 'data' in res.data && res.data.data?.list
      ? res.data.data
      : res?.data && 'list' in res.data
        ? res.data
        : res?.data;

  // const transformChannelData = (listData: any[]) => listData.map(createChannelTransform());

  const transformConversation = (items: any[]) =>
    items.map((item: any) => ({
      ...item,
      displayName:
        item.displayName ||
        `${item.contact?.firstName || ''} ${item.contact?.lastName || ''}`.trim() ||
        item.contactIdentifier ||
        item.id,
      lastMessage:
        (typeof item.lastMessage === 'object' ? item.lastMessage?.content : item.lastMessage) ||
        '-',
      status: item.status || '-',
      createdAt: item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY HH:mm') : '-',
    }));

  // Fetch table data
  const fetchTableData = async () => {
    try {
      tableConfig.setLoader(true);
      const apiFunc = getApiFunction();
      if (!apiFunc) throw new Error(`Filter API not found for type: ${type}`);

      const queryParamsWithFilters =
        isAccountView && currentContactId && type === 'Contact'
          ? {
              ...tableConfig.queryParams,
              filters: [
                ...tableConfig.queryParams.filters,
                { fieldname: 'companyId', search: currentContactId },
              ],
            }
          : tableConfig.queryParams;

      const res: any = await apiFunc(queryParamsWithFilters);
      const dataRoot = processApiResponse(res);
      tableConfig.setTotalCount(dataRoot?.total || 0);
      const listData = dataRoot?.list || [];

      let processedData =
        (await Promise.all(
          listData.map(async (data: any) => ({
            ...data,
            ProfileURL: data.ProfileURL ? await getProfileUrl(data.ProfileURL) : '',
          }))
        )) || [];

      if (type === 'Conversation') {
        processedData = transformConversation(processedData);
      }

      setTableData(processedData);
      tableConfig.setLoader(false);
    } catch (err) {
      console.error('Error filtering records:', { err, type });
      toast.error(t('RECORD_FETCHING_FAILED'));
      tableConfig.setLoader(false);
    }
  };

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    if (!searchInput || searchInput.length < 2) {
      setDropdownData([]);
      return;
    }
    try {
      setIsDropdownLoading(true);
      const apiFunc = getApiFunction();
      const queryParamsWithFilters =
        isAccountView && currentContactId && type === 'Contact'
          ? {
              ...tableConfig.queryParams,
              filters: [
                ...tableConfig.queryParams.filters,
                { fieldname: 'companyId', search: currentContactId },
              ],
            }
          : tableConfig.queryParams;

      const res: any = await apiFunc({
        ...queryParamsWithFilters,
        limit: 100,
        globalSearch: searchInput,
      });

      const listData =
        res?.data && 'data' in res.data && res.data.data?.list
          ? res.data.data.list
          : res?.data?.list || [];

      if (type === 'Channel' || type === 'Conversation') {
        let list = res?.data?.list ?? res?.list ?? [];
        if (type === 'Conversation') {
          list = transformConversation(list);
        }
        setDropdownData(list || []);
      } else {
        setDropdownData(listData || []);
      }
    } catch (err) {
      console.error('Dropdown fetch failed:', err);
    } finally {
      setIsDropdownLoading(false);
    }
  };

  // Fetch default record
  const fetchDefaultRecord = async () => {
    if (!defaultValue || lastFetchedIdRef.current === defaultValue) return;
    lastFetchedIdRef.current = defaultValue;

    try {
      const apiFuncObj = {
        Account: getAccountDtl,
        Channel: filterChannel,
        Contact: getContactDtl,
        Conversation: getConversationDtl,
      };
      const apiFunc = apiFuncObj[type as keyof typeof apiFuncObj];
      if (!apiFunc) throw new Error(`Filter API not found for type: ${type}`);

      const res: any = await apiFunc(
        (type === 'Conversation'
          ? defaultValue
          : type === 'Team'
            ? { id: defaultValue }
            : defaultValue) as any
      );

      const data = res?.data?.data || res?.data || res;
      let record = data;

      if (type === 'Conversation') record = data.conversation || data;
      if (type === 'Contact') record = data.contact || data;
      if (type === 'Account') record = data.company || data;
      if (type === 'Team') record = data.team || data;

      // Handle the case where the conversation list might return the object directly or nested
      if (type === 'Conversation' && record) {
        record = {
          ...record,
          displayName:
            record.displayName ||
            (record.contact
              ? `${record.contact.firstName || ''} ${record.contact.lastName || ''}`.trim()
              : null) ||
            record.contactIdentifier ||
            record.id,
        };
      }

      if (record) {
        handleItemSelect(record);
      }
    } catch (err) {
      console.error('Failed to fetch default record:', err);
    }
  };

  const handleItemSelect = async (item: any) => {
    let selectedItem = item;

    // Fetch full details for conversation to ensure nested objects like contact/channel are present
    if (type === 'Conversation' && item?.id) {
      if (lastFetchedIdRef.current !== item.id) {
        lastFetchedIdRef.current = item.id;
        try {
          const res: any = await getConversationDtl(item.id);
          const record = res?.data?.data?.conversation || res?.data?.data || res?.data || res;
          if (record) {
            selectedItem = {
              ...record,
              displayName:
                record.displayName ||
                (record.contact
                  ? `${record.contact.firstName || ''} ${record.contact.lastName || ''}`.trim()
                  : null) ||
                record.contactIdentifier ||
                record.id,
            };
          }
        } catch (error) {
          console.error('Failed to fetch conversation details:', error);
        }
      }
    }

    const labelVal =
      selectedItem?.displayName ??
      selectedItem?.name ??
      (selectedItem?.firstName || selectedItem?.lastName
        ? `${selectedItem.firstName ?? ''} ${selectedItem.lastName ?? ''}`.trim()
        : null) ??
      selectedItem?.email ??
      selectedItem?.channelName ??
      '-';
    setSelectedLabel(labelVal);
    setValue(name, selectedItem?.id, { shouldValidate: true });
    setSelectedUserAccDtl({ label: labelVal, value: selectedItem?.id });
    if (onItemSelect) onItemSelect(selectedItem);
    setDropdownData([]);
    setSearchInput('');
  };

  const handleRecordSelect = (row: any) => {
    lastFetchedIdRef.current = row?.id;
    handleItemSelect(row);
    setOpenDialog(false);
  };

  // Effects
  useEffect(() => {
    if (openDialog) {
      fetchTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig.queryParams, isAccountView, currentContactId, openDialog]);

  useEffect(() => {
    if (!openDialog) {
      tableConfig.setLoader(false);
    }
  }, [openDialog, tableConfig]);

  useEffect(() => {
    const debounce = setTimeout(fetchDropdownData, 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    if (!defaultValue) {
      setValue(name, '');
      setSelectedLabel('');
      return;
    }
    fetchDefaultRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return {
    // State
    openDialog,
    searchInput,
    dropdownData,
    selectedLabel,
    isDropdownLoading,
    tableData,
    isLoader,
    tableConfig,
    t,

    // Actions
    setOpenDialog,
    setSearchInput,
    setSelectedLabel,
    setDropdownData,
    handleItemSelect,
    handleRecordSelect,
  };
}
